import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { ArrowLeft, ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { NavGroup, RailLink, type NavGroupSection } from "@/components/app-shell/RailNav";

export interface SuiteItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  active: boolean;
  badge?: ReactNode;
}

export interface SuiteGroup {
  id: string;
  label?: string;
  items: SuiteItem[];
}

export interface Suite {
  id: string;
  label: string;
  icon: LucideIcon;
  groups: SuiteGroup[];
  landingPath?: string;
  dataTour?: string;
  routePrefixes?: string[];
}

export interface SuiteLink {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  active: boolean;
  dataTour?: string;
}

export type SuiteNavEntry =
  | { kind: "link"; link: SuiteLink }
  | { kind: "suite"; suite: Suite }
  | { kind: "divider"; id: string };

export interface SuiteNavProps {
  entries: SuiteNavEntry[];
  collapsed: boolean;
  pathname: string;
  onSelect: (path: string) => void;
}

type Direction = "forward" | "back" | null;

const ROW = "flex h-10 w-full items-center rounded-lg px-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0";
const ROW_IDLE = "text-ink-700 hover:bg-black/[0.04] hover:text-ink-900 dark:hover:bg-white/[0.06]";
const ROW_ACTIVE = "bg-primary/10 font-medium text-primary";

const hasItems = (suite: Suite) => suite.groups.some((g) => g.items.length > 0);

function tidy(entries: SuiteNavEntry[]): SuiteNavEntry[] {
  const visible = entries.filter((e) => e.kind !== "suite" || hasItems(e.suite));
  const out: SuiteNavEntry[] = [];
  for (const entry of visible) {
    if (entry.kind === "divider" && (out.length === 0 || out[out.length - 1].kind === "divider")) continue;
    out.push(entry);
  }
  while (out.length > 0 && out[out.length - 1].kind === "divider") out.pop();
  return out;
}

function suiteForRoute(suites: Suite[], pathname: string): string | null {
  const byItem = suites.find((s) => s.groups.some((g) => g.items.some((i) => i.active)));
  if (byItem) return byItem.id;
  let best: { id: string; length: number } | null = null;
  for (const suite of suites) {
    for (const prefix of suite.routePrefixes ?? []) {
      const matches = pathname === prefix || pathname.startsWith(`${prefix}/`);
      if (matches && (!best || prefix.length > best.length)) best = { id: suite.id, length: prefix.length };
    }
  }
  return best?.id ?? null;
}

function followLink(event: MouseEvent<HTMLAnchorElement>, go: () => void) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  go();
}

function PanelLink({ item, onSelect }: { item: SuiteItem; onSelect: (path: string) => void }) {
  return (
    <a
      href={item.path}
      aria-current={item.active ? "page" : undefined}
      onClick={(e) => followLink(e, () => onSelect(item.path))}
      className={cn(ROW, "h-9 gap-2 text-[15px]", item.active ? ROW_ACTIVE : ROW_IDLE)}
    >
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.badge}
    </a>
  );
}

function TopLink({ link, onSelect }: { link: SuiteLink; onSelect: (path: string) => void }) {
  return (
    <a
      href={link.path}
      data-tour={link.dataTour}
      aria-current={link.active ? "page" : undefined}
      onClick={(e) => followLink(e, () => onSelect(link.path))}
      className={cn(ROW, "text-[16px]", link.active ? ROW_ACTIVE : ROW_IDLE)}
    >
      {link.label}
    </a>
  );
}

function CollapsedRail({ entries, onSelect }: { entries: SuiteNavEntry[]; onSelect: (path: string) => void }) {
  return (
    <>
      {entries.map((entry) => {
        if (entry.kind === "divider") return null;
        if (entry.kind === "link") {
          const { link } = entry;
          return (
            <RailLink
              key={link.id}
              icon={link.icon}
              label={link.label}
              dataTour={link.dataTour}
              active={link.active}
              collapsed
              onSelect={() => onSelect(link.path)}
            />
          );
        }
        const { suite } = entry;
        const items = suite.groups.flatMap((g) => g.items);
        const sections: NavGroupSection[] = suite.groups
          .filter((g) => g.items.length > 0)
          .map((g) => ({ id: g.id, label: g.label, items: g.items }));
        return (
          <NavGroup
            key={suite.id}
            icon={suite.icon}
            label={suite.label}
            dataTour={suite.dataTour}
            active={items.some((i) => i.active)}
            collapsed
            sections={sections}
            isItemActive={(sub) => items.some((i) => i.id === sub.id && i.active)}
            onSelect={onSelect}
          />
        );
      })}
    </>
  );
}

export function SuiteNav({ entries, collapsed, pathname, onSelect }: SuiteNavProps) {
  const tidied = tidy(entries);
  const suites = tidied.flatMap((e) => (e.kind === "suite" ? [e.suite] : []));
  const activeSuiteId = suiteForRoute(suites, pathname);

  const [openId, setOpenId] = useState<string | null>(activeSuiteId);
  const [direction, setDirection] = useState<Direction>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    setOpenId(activeSuiteId);
  }, [activeSuiteId]);

  const open = suites.find((s) => s.id === openId) ?? null;
  const openKey = open?.id ?? null;

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (openKey) backRef.current?.focus({ preventScroll: true });
  }, [openKey]);

  if (collapsed) return <CollapsedRail entries={tidied} onSelect={onSelect} />;

  const openSuite = (suite: Suite) => {
    setDirection("forward");
    setOpenId(suite.id);
  };
  const goBack = () => {
    setDirection("back");
    setOpenId(null);
  };

  const enter = direction === "forward"
    ? "motion-safe:animate-suite-in-right"
    : direction === "back"
      ? "motion-safe:animate-suite-in-left"
      : "";

  if (open) {
    return (
      <div key={open.id} className={cn("space-y-1", enter)} data-suite-panel={open.id}>
        <button
          ref={backRef}
          type="button"
          onClick={goBack}
          aria-label={`Volver al menú principal desde ${open.label}`}
          className="mb-1 flex h-11 w-full items-center gap-2 rounded-lg px-3 text-left text-[18px] font-bold text-ink-900 transition-colors hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-white/[0.06]"
        >
          <ArrowLeft className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
          <span className="truncate">{open.label}</span>
        </button>
        {open.groups
          .filter((g) => g.items.length > 0)
          .map((group) => (
            <section key={group.id} aria-label={group.label ?? open.label} className="space-y-0.5 pb-2">
              {group.label && (
                <h3 className="px-3 pb-1 pt-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-mute">{group.label}</h3>
              )}
              {group.items.map((item) => (
                <PanelLink key={item.id} item={item} onSelect={onSelect} />
              ))}
            </section>
          ))}
      </div>
    );
  }

  return (
    <div key="root" className={cn("space-y-0.5", enter)} data-suite-root>
      {tidied.map((entry) => {
        if (entry.kind === "divider") {
          return <div key={entry.id} role="separator" className="mx-3 my-2 border-t border-sidebar-border" />;
        }
        if (entry.kind === "link") return <TopLink key={entry.link.id} link={entry.link} onSelect={onSelect} />;
        const { suite } = entry;
        const landing = suite.landingPath;
        return (
          <button
            key={suite.id}
            type="button"
            data-tour={suite.dataTour}
            aria-label={`Abrir ${suite.label}`}
            onClick={() => {
              openSuite(suite);
              if (landing) onSelect(landing);
            }}
            className={cn(ROW, "justify-between text-[16px]", ROW_IDLE)}
          >
            <span className="truncate">{suite.label}</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-mute" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
